import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { MenuItem, TextField, Box, InputAdornment } from '@mui/material';
import { ScenarioSettingsLanguageVals } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/types.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCoreNs } from '@/i18n/dictionaries/interfaces.ts';
import { LanguageFlag } from '../ScenarioEditorPage/ScenarioSettingsDialog/ScenarioGeneralSettings/LanguageFlag.tsx';

// Helper function to map ScenarioSettingsLanguageVals to language codes for flag display
const getLanguageCodeForFlag = (langValue: string): string => {
  switch (langValue) {
    case ScenarioSettingsLanguageVals.Norwegian:
      return 'no-NO';
    case ScenarioSettingsLanguageVals.English:
      return 'en-US';
    case ScenarioSettingsLanguageVals.EnglishUk:
      return 'en-GB';
    case ScenarioSettingsLanguageVals.German:
      return 'de-DE';
    case ScenarioSettingsLanguageVals.Dutch:
      return 'nl-NL';
    case ScenarioSettingsLanguageVals.French:
      return 'fr-FR';
    case ScenarioSettingsLanguageVals.Italian:
      return 'it-IT';
    case ScenarioSettingsLanguageVals.Danish:
      return 'da-DK';
    case ScenarioSettingsLanguageVals.Swedish:
      return 'sv-SE';
    case ScenarioSettingsLanguageVals.Icelandic:
      return 'is-IS';
    case ScenarioSettingsLanguageVals.EnglishAustralia:
      return 'en-AU';
    case ScenarioSettingsLanguageVals.SpanishSpain:
      return 'es-ES';
    case ScenarioSettingsLanguageVals.SpanishMexico:
      return 'es-MX';
    case ScenarioSettingsLanguageVals.SpanishUS:
      return 'es-US';
    case ScenarioSettingsLanguageVals.ArabicUae:
      return 'ar-AE';
    case ScenarioSettingsLanguageVals.Hindi:
      return 'hi-IN';
    default:
      return langValue;
  }
};

export interface LanguageSelectorProps {
  name: string;
  control: any;
  label?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  'data-cy'?: string;
  className?: string;
  onChange?: (value: string) => void;
}

/**
 * A reusable language selector component.
 * Uses the 'as' prop pattern for react-hook-form Controller.
 * Uses i18n translations for language names and supports all languages defined in ScenarioSettingsLanguageVals.
 */
export default function LanguageSelector({
  name,
  control,
  label,
  disabled = false,
  fullWidth = true,
  error = false,
  helperText,
  'data-cy': dataCy = 'listOfLanguage',
  className,
  onChange
}: LanguageSelectorProps): JSX.Element {
  const [t] = useTranslation([I18nNamespace.Core]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          margin="normal"
          select
          variant="outlined"
          disabled={disabled}
          label={label}
          fullWidth={fullWidth}
          data-cy={dataCy}
          error={error}
          helperText={helperText}
          className={className}
          value={field.value}
          onChange={(e) => {
            field.onChange(e);
            onChange && onChange(e.target.value);
          }}
          onBlur={field.onBlur}
          name={field.name}
          ref={field.ref}
        >
          <MenuItem
            key={ScenarioSettingsLanguageVals.Norwegian}
            data-cy="Norwegian"
            value={ScenarioSettingsLanguageVals.Norwegian}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="no-NO" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.norwegian))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.English}
            data-cy="English (US)"
            value={ScenarioSettingsLanguageVals.English}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="en-US" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.englishus))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.EnglishUk}
            data-cy="English (UK)"
            value={ScenarioSettingsLanguageVals.EnglishUk}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="en-GB" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.englishuk))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.German}
            data-cy="German"
            value={ScenarioSettingsLanguageVals.German}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="de-DE" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.german))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.Dutch}
            data-cy="Dutch"
            value={ScenarioSettingsLanguageVals.Dutch}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="nl-NL" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.dutch))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.French}
            data-cy="French"
            value={ScenarioSettingsLanguageVals.French}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="fr-FR" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.french))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.Italian}
            data-cy="Italian"
            value={ScenarioSettingsLanguageVals.Italian}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="it-IT" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.italian))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.Danish}
            data-cy="Danish"
            value={ScenarioSettingsLanguageVals.Danish}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="da-DK" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.danish))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.Swedish}
            data-cy="Swedish"
            value={ScenarioSettingsLanguageVals.Swedish}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="sv-SE" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.swedish))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.Icelandic}
            data-cy="Icelandic"
            value={ScenarioSettingsLanguageVals.Icelandic}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="is-IS" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.icelandic))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.EnglishAustralia}
            data-cy="English (Australia)"
            value={ScenarioSettingsLanguageVals.EnglishAustralia}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="en-AU" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.englishAustralia))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.SpanishSpain}
            data-cy="Spanish (Spain)"
            value={ScenarioSettingsLanguageVals.SpanishSpain}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="es-ES" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.spanishSpain))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.SpanishMexico}
            data-cy="Spanish (Mexico)"
            value={ScenarioSettingsLanguageVals.SpanishMexico}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="es-MX" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.spanishMexico))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.SpanishUS}
            data-cy="Spanish (US)"
            value={ScenarioSettingsLanguageVals.SpanishUS}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="es-US" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.spanishUs))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.ArabicUae}
            data-cy="Arabic (UAE)"
            value={ScenarioSettingsLanguageVals.ArabicUae}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="ar-AE" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.arabicUae))}
            </Box>
          </MenuItem>
          <MenuItem
            key={ScenarioSettingsLanguageVals.Hindi}
            data-cy="Hindi"
            value={ScenarioSettingsLanguageVals.Hindi}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageFlag languageCode="hi-IN" size="small" />
              {t(nameof.full<I18nCoreNs>((l) => l.languagePicker.hindi))}
            </Box>
          </MenuItem>
        </TextField>
      )}
    />
  );
}
